/* ── Birch BabyLog — Unified 0–24 Month Daily Care Tracker ─────────────── */
'use strict';

const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition || null;

// ── Storage keys ──────────────────────────────────────────────────────────────
const DB_KEY     = 'bl_logs';
const PREFS_KEY  = 'bl_prefs';
const ACTIVE_KEY = 'bl_active_tt';

// ── Default prefs ─────────────────────────────────────────────────────────────
const DEFAULT_PREFS = {
  babyName:                '',
  birthdate:               null,
  gestationalAge:          40,
  onboardingDone:          false,
  pottyActivated:          false,
  activePlayMode:          false,
  activePlayGoalMin:       null,    // null = use stage default
  activePlayGoalCustom:    false,
  diaperArchived:          false,
  refluxResolutionSeen:    false,
  activePlayTransitionSeen: false,
  pottyPromptSeen:         false,
  pottyPromptDismissedUntil: null,
  diaperArchivePromptSeen: false,
};

// ── Stage definitions ─────────────────────────────────────────────────────────
const STAGES = [
  { id: 1, label: 'Newborn',       min: 0,  max: 3  },
  { id: 2, label: 'Early Infant',  min: 3,  max: 6  },
  { id: 3, label: 'Late Infant',   min: 6,  max: 12 },
  { id: 4, label: 'Early Toddler', min: 12, max: 18 },
  { id: 5, label: 'Late Toddler',  min: 18, max: 25 },
];

// Active time daily goals (minutes) per stage
const ACTIVE_GOALS = { 1: 30, 2: 30, 3: 30, 4: 45, 5: 60 };

// ── State ─────────────────────────────────────────────────────────────────────
let logs     = [];
let prefs    = { ...DEFAULT_PREFS };
let activeTT = null;
let ttTick   = null;
let voiceRec = null;
let screen   = 'home';

// Overlay form state
let diaperType    = null;
let diaperColor   = null;
let rashOn        = false;
let leakOn        = false;
let pullUpOn      = false;
let refluxVol     = null;
let refluxPos     = null;
let isIllness     = false;
let bathType      = 'sponge';
let bathRoutine   = false;
let activePos     = null;
let activeMood    = null;
let pottyType     = null;
let pottyLoc      = 'potty';
let pottyInit     = 'parent';
let pottyDetailsOpen = false;

// ── DOM helpers ───────────────────────────────────────────────────────────────
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

// ── Storage ───────────────────────────────────────────────────────────────────
function loadLogs() {
  try { logs = JSON.parse(localStorage.getItem(DB_KEY) || '[]'); }
  catch { logs = []; }
}
function saveLogs() { localStorage.setItem(DB_KEY, JSON.stringify(logs)); syncToCloud(); }

function loadPrefs() {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    prefs = { ...DEFAULT_PREFS, ...saved };
  } catch { prefs = { ...DEFAULT_PREFS }; }
}
function savePrefs() { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); syncToCloud(); }

function loadActiveTT() {
  try { return JSON.parse(localStorage.getItem(ACTIVE_KEY) || 'null'); }
  catch { return null; }
}
function saveActiveTT(state) {
  if (state) localStorage.setItem(ACTIVE_KEY, JSON.stringify(state));
  else localStorage.removeItem(ACTIVE_KEY);
}

// ── Cloud sync (birch_tracker.baby-log metafield) ─────────────────────────────
const CLOUD_BASE = '/apps/ai/tracker/baby-log';

// Active child context (set after boot from shared profile)
let _blCtx = { type: 'own', childName: null, childId: null, ownerId: null };
let _blAllContexts = [];

function cloudApi() {
  if (_blCtx.type === 'shared' && _blCtx.ownerId) {
    return `/apps/ai/tracker-caregivers/data/baby-log?ownerId=${encodeURIComponent(_blCtx.ownerId)}`;
  }
  return _blCtx.childId ? `${CLOUD_BASE}?childId=${encodeURIComponent(_blCtx.childId)}` : CLOUD_BASE;
}

// Alias so existing references to CLOUD_API still work
const CLOUD_API = CLOUD_BASE;
const TRIM_MS   = 30 * 24 * 60 * 60 * 1000;
let _syncTimer  = null;

function _cloudPayload() {
  const cutoff = Date.now() - TRIM_MS;
  return {
    logs:      logs.filter(l => l.loggedAt >= cutoff),
    prefs,
    updatedAt: Date.now(),
  };
}

function syncToCloud() {
  clearTimeout(_syncTimer);
  _syncTimer = setTimeout(() => {
    fetch(cloudApi(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(_cloudPayload()),
    }).catch(() => {});
  }, 400);
}

async function loadFromCloud() {
  try {
    const r = await fetch(cloudApi(), { credentials: 'same-origin' });
    if (!r.ok) return;
    const { data } = await r.json();
    if (!data || typeof data !== 'object') return;
    if (Array.isArray(data.logs)) { logs = data.logs; localStorage.setItem(DB_KEY, JSON.stringify(logs)); }
    if (data.prefs && typeof data.prefs === 'object') { prefs = { ...DEFAULT_PREFS, ...data.prefs }; localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); }
  } catch {}
}

// ── ID + time helpers ─────────────────────────────────────────────────────────
function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}
function msToMMSS(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
}
/** Format fractional minutes as "Xm Ys" — no rounding up, seconds preserved. */
function formatActiveDuration(mins) {
  const totalSec = Math.round(mins * 60);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m} min`;
  return `${m}m ${s}s`;
}
function formatTimeAgo(ts) {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem === 0 ? `${h}h ago` : `${h}h ${rem}m ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d}d ago`;
}
function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
function formatDate(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}
function todayStart() {
  const d = new Date(); d.setHours(0,0,0,0); return d.getTime();
}
function todayLogs() {
  return logs.filter(l => (l.loggedAt || l.startTime || 0) >= todayStart());
}
function logsOfType(type, days = 7) {
  const since = Date.now() - days * 86400000;
  return logs.filter(l => l.type === type && (l.loggedAt || l.startTime || 0) >= since);
}

// ── Age + stage ───────────────────────────────────────────────────────────────
function getAgeMonths() {
  if (!prefs.birthdate) return 0;
  const weeksEarly = Math.max(0, 40 - (prefs.gestationalAge || 40));
  const adjusted = new Date(new Date(prefs.birthdate).getTime() + weeksEarly * 7 * 86400000);
  const now = new Date();
  return (now.getFullYear() - adjusted.getFullYear()) * 12
       + (now.getMonth() - adjusted.getMonth())
       + (now.getDate() < adjusted.getDate() ? -1 : 0);
}

function getStage() {
  const m = getAgeMonths();
  return STAGES.find(s => m >= s.min && m < s.max)?.id || (m >= 18 ? 5 : 1);
}

function getActiveGoal(stage) {
  if (prefs.activePlayGoalCustom && prefs.activePlayGoalMin) return prefs.activePlayGoalMin;
  return ACTIVE_GOALS[stage] || 30;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── Onboarding ────────────────────────────────────────────────────────────────
async function hydrateFromSharedProfile() {
  if (!window.BirchChildren) return prefs.onboardingDone;
  try {
    const BC  = window.BirchChildren;
    const BTC = window.BirchCaregivers;
    const allCtxs = BTC ? await BTC.getAllContexts('baby-log') : (await BC.load()).map(c => ({
      type: 'own', childName: c.name, childId: c.id, dob: c.dob, ownerId: null, ownerName: null, label: c.name
    }));
    _blAllContexts = allCtxs;

    if (allCtxs.length === 0) return false;
    const ctx = allCtxs.length === 1 ? allCtxs[0]
      : (prefs.onboardingDone ? allCtxs[0] : await BC.pickContext(allCtxs, { title: 'Whose log is this?' }));
    if (!ctx) return prefs.onboardingDone;

    _blCtx              = ctx;
    prefs.babyName       = ctx.childName || prefs.babyName;
    prefs.birthdate      = ctx.dob       || prefs.birthdate;
    prefs.onboardingDone = true;
    savePrefs();
    updateChildSelectorLabel();
    updateOverlayChildChips();
    return true;
  } catch (e) { return false; }
}

function updateChildSelectorLabel() {
  const name = _blCtx.childName || prefs.babyName || 'Select child';
  const label = $('#bl-child-selector-label');
  const wrap  = $('#bl-child-selector-wrap');
  if (label) label.textContent = name;
  if (wrap)  wrap.style.display = _blAllContexts.length > 1 ? '' : 'none';
}

function updateOverlayChildChips() {
  var name = _blCtx.childName || prefs.babyName || '';
  document.querySelectorAll('.bl-child-chip-label').forEach(function(el) {
    el.textContent = name || 'Select child';
  });
  // Show chips only when multiple contexts exist
  document.querySelectorAll('.bl-child-chip-wrap').forEach(function(el) {
    el.style.display = _blAllContexts.length > 1 ? '' : 'none';
  });
}

async function switchLogChild() {
  if (!window.BirchChildren || _blAllContexts.length < 2) return;
  const ctx = await window.BirchChildren.pickContext(_blAllContexts, { title: 'Whose log is this?' });
  if (!ctx) return;
  _blCtx          = ctx;
  prefs.babyName  = ctx.childName || prefs.babyName;
  prefs.birthdate = ctx.dob || prefs.birthdate;
  savePrefs();
  updateChildSelectorLabel();
  updateOverlayChildChips();
  applyStageUI(getStage());
  renderHome();
}

function checkOnboarding() {
  if (!prefs.onboardingDone) {
    const ob = $('#bl-onboarding');
    if (ob) ob.classList.add('bl-onboarding--active');
  }
}

function saveOnboarding() {
  const name      = $('#bl-ob-name')?.value.trim();
  const birthdate = $('#bl-ob-birthdate')?.value;
  const gesta     = parseInt($('#bl-ob-gestational')?.value || '40', 10);
  if (!name || !birthdate) {
    showToast('Please enter a name and birthdate.');
    return;
  }
  prefs.babyName       = name;
  prefs.birthdate      = birthdate;
  prefs.gestationalAge = gesta || 40;
  prefs.onboardingDone = true;
  savePrefs();

  // Sync to shared profile
  if (window.BirchChildren) {
    window.BirchChildren.upsert({ id: window.BirchChildren.newId(), name: name, dob: birthdate, gestationalAge: gesta || 40 }).catch(function(){});
  }

  const ob = $('#bl-onboarding');
  if (ob) ob.classList.remove('bl-onboarding--active');
  updateChildSelectorLabel();
  applyStageUI(getStage());
  renderHome();
}

// ── Stage UI ──────────────────────────────────────────────────────────────────
function applyStageUI(stage) {
  const name = prefs.babyName || 'Daily care';
  const badge = $('#bl-stage-badge');
  const greet  = $('#bl-topbar-greeting');
  const nameEl = $('#bl-topbar-name');
  if (greet)  greet.textContent  = greeting();
  if (nameEl) nameEl.textContent = `${name}'s log`;
  if (badge) {
    badge.textContent = STAGES.find(s => s.id === stage)?.label || '';
    badge.style.display = 'inline-flex';
  }

  // Active time label (Tummy Time → Active Play at stage 3+)
  const isActivePlay = stage >= 3 && prefs.activePlayMode;
  const activeLabel  = isActivePlay ? 'Active Play' : 'Tummy Time';
  $$('#bl-active-btn-label, #bl-tummy-overlay-title, #bl-sec-active-label, #bl-pick-active-label')
    .forEach(el => { if (el) el.textContent = activeLabel; });

  // Stage 1-3: 2×2 grid; Stage 4-5: potty hero + secondary grid
  const grid13   = $('#bl-grid-s13');
  const grid45   = $('#bl-grid-s45');
  const pottyHero = $('#bl-potty-hero');
  const moreBtn   = $('#bl-more-btn');

  if (stage <= 3) {
    if (grid13)    grid13.style.display    = 'grid';
    if (grid45)    grid45.style.display    = 'none';
    if (pottyHero) pottyHero.style.display = 'none';
    if (moreBtn)   moreBtn.style.display   = 'none';
  } else {
    if (grid13)    grid13.style.display    = 'none';
    if (pottyHero) pottyHero.style.display = prefs.pottyActivated ? 'block' : 'none';
    if (grid45)    grid45.style.display    = 'grid';
    if (moreBtn)   moreBtn.style.display   = 'block';
  }

  // Reflux fading visual (stage 3+, dim if <2 logs in 21 days)
  const refluxBtn = $('#bl-btn-reflux');
  if (refluxBtn) {
    refluxBtn.classList.toggle('bl-grid-btn--dimmed', stage >= 3 && isRefluxFading());
  }

  // Pull-up toggle (stage 4+)
  const pullupRow = $('#bl-diaper-pullup-row');
  if (pullupRow) pullupRow.style.display = stage >= 4 ? 'grid' : 'none';

  // Illness toggle (stage 3+)
  const illnessRow = $('#bl-reflux-illness-row');
  if (illnessRow) illnessRow.style.display = stage >= 3 ? 'block' : 'none';

  // Sink bath (stage 3+), shower (stage 5+)
  const sinkBtn   = $('#bl-bath-sink-btn');
  const showerBtn = $('#bl-bath-shower-btn');
  if (sinkBtn)   sinkBtn.style.display   = stage >= 3 ? 'block' : 'none';
  if (showerBtn) showerBtn.style.display = stage >= 5 ? 'block' : 'none';

  // Bedtime routine toggle (stage 4+)
  const bathRoutineRow = $('#bl-bath-routine-row');
  if (bathRoutineRow) bathRoutineRow.style.display = stage >= 4 ? 'block' : 'none';

  // Active play positions
  const posS12  = $('#bl-tummy-pos-s12');
  const posS345 = $('#bl-tummy-pos-s345');
  if (posS12)  posS12.style.display  = stage <= 2 ? 'block' : 'none';
  if (posS345) posS345.style.display = stage >= 3 ? 'block' : 'none';

  // Potty button in picker + history chip
  const pickPotty = $('#bl-pick-potty');
  const chipPotty = $('#bl-chip-potty');
  const visible   = stage >= 4;
  if (pickPotty) pickPotty.style.display = visible ? 'flex' : 'none';
  if (chipPotty) chipPotty.style.display = visible ? 'inline-flex' : 'none';

  // Settings section visibility
  const pottySec  = $('#bl-settings-potty-section');
  const diaperSec = $('#bl-settings-diaper-section');
  if (pottySec)  pottySec.style.display  = stage >= 4 ? 'block' : 'none';
  if (diaperSec) diaperSec.style.display = stage >= 5 ? 'block' : 'none';

  // Potty row in today strip
  const pottyRow = $('#bl-today-row-potty');
  const refluxRow = $('#bl-today-row-reflux');
  if (pottyRow)  pottyRow.style.display  = stage >= 4 ? 'flex' : 'none';
  if (refluxRow) refluxRow.style.display = stage <= 3 ? 'flex' : 'none';

  // Daily goal display
  const goal = getActiveGoal(stage);
  const goalText = `0 / ${goal} min`;
  const progressText = $('#bl-active-progress-text');
  if (progressText) progressText.textContent = goalText;
}

// ── Context cards ─────────────────────────────────────────────────────────────
function renderContextCards(stage) {
  const container = $('#bl-context-cards');
  if (!container) return;
  container.innerHTML = '';
  const cards = [];

  // Reflux resolution card (stage 3+, shown once)
  if (stage >= 3 && !prefs.refluxResolutionSeen && isRefluxResolving()) {
    cards.push({
      key: 'reflux-resolution',
      type: 'resolution',
      title: 'Reflux improving',
      body: `${prefs.babyName || 'Your baby'}'s spit-ups have really reduced over the last 3 weeks — that's great progress. Reflux typically resolves between 9–12 months. The log stays open if you need it.`,
      cta: null,
      dismiss: 'Got it',
    });
  }

  // Active play transition card (stage 3, shown once after activePlayMode activates)
  if (stage === 3 && prefs.activePlayMode && !prefs.activePlayTransitionSeen) {
    cards.push({
      key: 'active-play-transition',
      type: 'transition',
      title: 'Tummy time → Active Play',
      body: `${prefs.babyName || 'Baby'} is on the move — that's tummy time in action. The goal's the same (active floor time), it just looks different now. We've updated the tracker to match.`,
      cta: null,
      dismiss: 'Got it',
    });
  }

  // Goal update banner when stage changes (show once per goal tier)
  const goal = getActiveGoal(stage);
  const goalBannerKey = `goal-banner-${stage}`;
  if ([4,5].includes(stage) && !prefs[goalBannerKey]) {
    cards.push({
      key: goalBannerKey,
      type: 'goal',
      title: `Active time goal updated: ${goal} min/day`,
      body: stage === 4
        ? `As ${prefs.babyName || 'baby'} starts walking and exploring, we've bumped the active time target to 45 min/day.`
        : `WHO recommends at least 60 min of active movement for toddlers. Here's ${prefs.babyName || 'baby'}'s updated target.`,
      cta: null,
      dismiss: 'Got it',
    });
  }

  // Potty training prompt (stage 4, 15 months, shown once unless snoozed)
  if (stage >= 4 && !prefs.pottyActivated && shouldShowPottyPrompt()) {
    cards.push({
      key: 'potty-prompt',
      type: 'potty',
      title: 'Potty training?',
      body: `Is ${prefs.babyName || 'your baby'} starting to show interest? We can track progress when you're ready. No pressure — most kids train between 18–36 months.`,
      cta: 'Start tracking',
      dismiss: 'Not yet',
    });
  }

  // Diaper archive prompt (stage 5, shown once)
  if (stage >= 5 && !prefs.diaperArchived && !prefs.diaperArchivePromptSeen && shouldPromptDiaperArchive()) {
    cards.push({
      key: 'diaper-archive',
      type: 'archive',
      title: 'Fewer diapers lately',
      body: `${prefs.babyName || 'Baby'} seems to be in nappies less often. Want to archive the diaper counter? History stays — it just won't show on the home screen.`,
      cta: 'Archive counter',
      dismiss: 'Keep showing',
    });
  }

  cards.forEach(card => {
    const el = document.createElement('div');
    el.className = `bl-context-card bl-context-card--${card.type}`;
    el.innerHTML = `
      <div class="bl-context-card__title">${card.title}</div>
      <div class="bl-context-card__body">${card.body}</div>
      <div class="bl-context-card__actions">
        ${card.cta ? `<button class="bl-context-card__cta" data-key="${card.key}">${card.cta}</button>` : ''}
        <button class="bl-context-card__dismiss" data-key="${card.key}">${card.dismiss}</button>
      </div>
    `;
    // CTA action
    el.querySelector('.bl-context-card__cta')?.addEventListener('click', () => {
      handleContextCTA(card.key);
    });
    // Dismiss action
    el.querySelector('.bl-context-card__dismiss')?.addEventListener('click', () => {
      handleContextDismiss(card.key);
      el.remove();
    });
    container.appendChild(el);
  });
}

function handleContextCTA(key) {
  if (key === 'potty-prompt') {
    activatePottyTracking();
  } else if (key === 'diaper-archive') {
    prefs.diaperArchived = true;
    prefs.diaperArchivePromptSeen = true;
    savePrefs();
    const el = $(`.bl-context-card__cta[data-key="${key}"]`)?.closest('.bl-context-card');
    if (el) el.remove();
    renderHome();
  }
}

function handleContextDismiss(key) {
  if (key === 'reflux-resolution') {
    prefs.refluxResolutionSeen = true;
  } else if (key === 'active-play-transition') {
    prefs.activePlayTransitionSeen = true;
  } else if (key === 'potty-prompt') {
    prefs.pottyPromptSeen = true;
    prefs.pottyPromptDismissedUntil = Date.now() + 30 * 86400000; // 30 days
  } else if (key === 'diaper-archive') {
    prefs.diaperArchivePromptSeen = true;
  } else if (key.startsWith('goal-banner-')) {
    prefs[key] = true;
  }
  savePrefs();
}

// ── Context helpers ───────────────────────────────────────────────────────────
function isRefluxFading() {
  const since = Date.now() - 21 * 86400000;
  return logs.filter(l => l.type === 'reflux' && l.loggedAt >= since).length < 2;
}

function isRefluxResolving() {
  if (getAgeMonths() < 6) return false;
  const weeks3 = Date.now() - 21 * 86400000;
  const recent = logs.filter(l => l.type === 'reflux' && l.loggedAt >= weeks3).length;
  return recent < 2 && logs.filter(l => l.type === 'reflux').length >= 5;
}

function shouldShowPottyPrompt() {
  if (prefs.pottyPromptSeen) return false;
  if (prefs.pottyPromptDismissedUntil && Date.now() < prefs.pottyPromptDismissedUntil) return false;
  return getAgeMonths() >= 15;
}

function shouldPromptDiaperArchive() {
  const since = Date.now() - 14 * 86400000;
  const recent = logs.filter(l => l.type === 'diaper' && l.loggedAt >= since).length;
  const dailyAvg = recent / 14;
  return dailyAvg < 1 && logs.filter(l => l.type === 'potty').length > 0;
}

function activatePottyTracking() {
  prefs.pottyActivated = true;
  savePrefs();
  const stage = getStage();
  const pottyHero = $('#bl-potty-hero');
  if (pottyHero && stage >= 4) pottyHero.style.display = 'block';
  const card = $(`.bl-context-card__cta[data-key="potty-prompt"]`)?.closest('.bl-context-card');
  if (card) card.remove();
  renderHome();
  showToast('Potty tracking activated ✓');
}

// ── Navigation ────────────────────────────────────────────────────────────────
function navigate(name, opts) {
  var pushHistory = !opts || opts.pushHistory !== false;
  var prev = screen;
  screen = name;
  $$('.bl-screen').forEach(s => s.classList.remove('bl-screen--active'));
  $$('.bl-nav-item').forEach(b => b.classList.remove('bl-nav-item--active'));
  const scr = $(`#bl-screen-${name}`);
  if (scr) scr.classList.add('bl-screen--active');
  const nav = $(`.bl-nav-item[data-screen="${name}"]`);
  if (nav) nav.classList.add('bl-nav-item--active');
  if (name === 'home')     renderHome();
  if (name === 'history')  renderHistory();
  if (name === 'insights') renderInsights();
  if (pushHistory && name !== 'home' && prev !== name) {
    history.pushState({ blScreen: name }, '');
  }
}

window.addEventListener('popstate', function(e) {
  if (e.state && e.state.blScreen) {
    navigate(e.state.blScreen, { pushHistory: false });
  } else if (screen !== 'home') {
    navigate('home', { pushHistory: false });
  }
});

function openOverlay(id) {
  const el = $(`#${id}`);
  if (el) el.classList.add('bl-overlay--active');
}
function closeOverlay(id) {
  const el = $(`#${id}`);
  if (el) el.classList.remove('bl-overlay--active');
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function showToast(msg, ms = 2800) {
  let t = $('#bl-toast');
  if (!t) {
    t = document.createElement('div');
    t.id = 'bl-toast';
    t.className = 'bl-toast';
    document.body.appendChild(t);
  }
  t.textContent = msg;
  t.classList.add('bl-toast--show');
  clearTimeout(t._timer);
  t._timer = setTimeout(() => t.classList.remove('bl-toast--show'), ms);
}

// ── HOME RENDER ───────────────────────────────────────────────────────────────
function renderHome() {
  const stage  = getStage();
  const today  = todayLogs();
  const goal   = getActiveGoal(stage);

  // Diaper strip
  const diapers = today.filter(l => l.type === 'diaper');
  const wet     = diapers.filter(l => l.diaperType === 'wet'   || l.diaperType === 'both').length;
  const dirty   = diapers.filter(l => l.diaperType === 'dirty' || l.diaperType === 'both').length;
  const diaperEl = $('#bl-today-diaper');
  if (diaperEl) {
    diaperEl.textContent = diapers.length
      ? `${wet} wet · ${dirty} dirty`
      : '— diapers today';
  }
  const diaperSub = $('#bl-diaper-btn-sub');
  if (diaperSub) diaperSub.textContent = diapers.length ? `${diapers.length} today` : 'Tap to log';

  // Reflux strip (stage 1-3)
  const refluxToday = today.filter(l => l.type === 'reflux');
  const lastReflux  = refluxToday.length ? refluxToday[refluxToday.length - 1] : null;
  const refluxEl    = $('#bl-today-reflux');
  if (refluxEl) {
    refluxEl.textContent = refluxToday.length
      ? `${refluxToday.length} spit-up${refluxToday.length > 1 ? 's' : ''} · last ${formatTimeAgo(lastReflux.loggedAt)}`
      : '— spit-ups today';
  }
  const refluxSub = $('#bl-reflux-btn-sub');
  if (refluxSub) refluxSub.textContent = refluxToday.length ? `${refluxToday.length} today` : 'Tap to log';

  // Potty strip (stage 4-5)
  const pottyToday = today.filter(l => l.type === 'potty');
  const pottyEl    = $('#bl-today-potty');
  if (pottyEl) {
    if (pottyToday.length) {
      const pee  = pottyToday.filter(l => l.pottyType === 'pee_success').length;
      const poo  = pottyToday.filter(l => l.pottyType === 'poo_success').length;
      const tries = pottyToday.filter(l => l.pottyType === 'attempt').length;
      const acc  = pottyToday.filter(l => l.pottyType === 'accident').length;
      const parts = [];
      if (pee)  parts.push(`${pee} pee ✓`);
      if (poo)  parts.push(`${poo} poo ✓`);
      if (tries) parts.push(`${tries} tr${tries > 1 ? 'ies' : 'y'}`);
      if (acc)  parts.push(`${acc} acc.`);
      pottyEl.textContent = parts.join(' · ');
    } else {
      pottyEl.textContent = '— potty today';
    }
  }

  // Potty hero card (stage 4-5)
  if (stage >= 4 && prefs.pottyActivated) {
    const pee  = pottyToday.filter(l => l.pottyType === 'pee_success').length;
    const poo  = pottyToday.filter(l => l.pottyType === 'poo_success').length;
    const att  = pottyToday.filter(l => l.pottyType === 'attempt').length;
    const acc  = pottyToday.filter(l => l.pottyType === 'accident').length;
    const set  = (id, val) => { const e = $(`#${id}`); if (e) e.textContent = val; };
    set('bl-ph-pee', pee);
    set('bl-ph-poo', poo);
    set('bl-ph-attempts', att);
    set('bl-ph-accidents', acc);
  }

  // Active time (tummy / active play)
  const completedActive = today.filter(l => l.type === 'tummy_time' && l.endTime);
  const activeMins = completedActive.reduce((s, l) => s + (l.durationMin || 0), 0);

  const progressText = $('#bl-active-progress-text');
  if (progressText) progressText.textContent = `${formatActiveDuration(activeMins)} / ${goal} min`;
  const bar = $('#bl-active-bar-fill');
  if (bar) bar.style.width = `${Math.min((activeMins / goal) * 100, 100)}%`;

  // Tummy ring in grid button
  const circ  = 2 * Math.PI * 24;
  const ttArc = $('#bl-tummy-ring-arc');
  if (ttArc) {
    const progress = Math.min(activeMins / goal, 1);
    ttArc.style.strokeDashoffset = circ * (1 - progress);
  }

  // Active button sub-text
  activeTT = loadActiveTT();
  const activeLabel = (stage >= 3 && prefs.activePlayMode) ? 'Active Play' : 'Tummy Time';
  const activeSub = $('#bl-active-btn-sub');
  const activeBtn = $('#bl-btn-tummy');
  const secActiveSub = $('#bl-sec-active-sub');
  if (activeTT) {
    if (activeSub)   activeSub.textContent = 'ACTIVE — tap to view';
    if (activeBtn)   activeBtn.classList.add('bl-grid-btn--active');
    const wrap = activeBtn?.querySelector('.bl-tummy-ring-wrap');
    if (wrap) wrap.classList.add('bl-tummy-ring-wrap--active');
    if (secActiveSub) secActiveSub.textContent = 'Active now';
  } else {
    if (activeSub)   activeSub.textContent = activeMins > 0 ? `${formatActiveDuration(activeMins)} today` : 'Start';
    if (activeBtn)   activeBtn.classList.remove('bl-grid-btn--active');
    const wrap = activeBtn?.querySelector('.bl-tummy-ring-wrap');
    if (wrap) wrap.classList.remove('bl-tummy-ring-wrap--active');
    if (secActiveSub) secActiveSub.textContent = activeMins > 0 ? formatActiveDuration(activeMins) : '—';
  }

  // Secondary grid diaper/bath subs
  const secDiaperSub = $('#bl-sec-diaper-sub');
  if (secDiaperSub) secDiaperSub.textContent = diapers.length ? `${diapers.length} today` : '—';

  // Bath strip
  const baths    = logs.filter(l => l.type === 'bath').sort((a, b) => b.loggedAt - a.loggedAt);
  const lastBath = baths[0] || null;
  const bathEl   = $('#bl-today-bath');
  if (bathEl) bathEl.textContent = lastBath ? `Last bath: ${formatTimeAgo(lastBath.loggedAt)}` : 'Last bath: —';
  const bathSub    = $('#bl-bath-btn-sub');
  const secBathSub = $('#bl-sec-bath-sub');
  if (bathSub)    bathSub.textContent    = lastBath ? formatTimeAgo(lastBath.loggedAt) : 'Tap to log';
  if (secBathSub) secBathSub.textContent = lastBath ? formatTimeAgo(lastBath.loggedAt) : '—';

  // More drawer reflux sub
  const moreReflux = $('#bl-more-reflux-sub');
  if (moreReflux) {
    moreReflux.textContent = refluxToday.length ? `${refluxToday.length} today` : 'Tap to log';
  }

  // Context cards
  renderContextCards(stage);
}

// ── HISTORY RENDER ────────────────────────────────────────────────────────────
function renderHistory(filter) {
  if (filter === undefined) {
    const active = $('.bl-filter-chip.bl-filter-chip--active');
    filter = active ? active.dataset.filter : 'all';
  }
  const list = $('#bl-history-list');
  if (!list) return;
  list.innerHTML = '';

  const filtered = logs
    .filter(l => filter === 'all' || l.type === filter)
    .sort((a, b) => (b.loggedAt || b.startTime || 0) - (a.loggedAt || a.startTime || 0));

  if (!filtered.length) {
    const empty = document.createElement('div');
    empty.className = 'bl-empty-state';
    empty.textContent = 'No entries yet. Tap a log button to get started.';
    list.appendChild(empty);
    return;
  }

  const groups = {};
  filtered.forEach(l => {
    const key = formatDate(l.loggedAt || l.startTime || Date.now());
    if (!groups[key]) groups[key] = [];
    groups[key].push(l);
  });

  Object.entries(groups).forEach(([date, entries]) => {
    const grp = document.createElement('div');
    grp.className = 'bl-history-group';
    const dateEl = document.createElement('div');
    dateEl.className = 'bl-history-date';
    dateEl.textContent = date;
    grp.appendChild(dateEl);

    entries.forEach(l => {
      const row = document.createElement('div');
      row.className = 'bl-history-row';
      row.innerHTML = `
        <div class="bl-history-icon bl-history-icon--${l.type}">${historyIcon(l.type)}</div>
        <div class="bl-history-meta">
          <div class="bl-history-title">
            ${historyTitle(l)}
            ${l.flagged ? '<span class="bl-history-flag">🚩</span>' : ''}
            ${l.isIllness ? '<span class="bl-history-illness-tag">illness</span>' : ''}
          </div>
          <div class="bl-history-sub">${historySub(l)}</div>
        </div>
        <div class="bl-history-time">${formatTime(l.loggedAt || l.startTime || Date.now())}</div>
        <button class="bl-history-delete" data-id="${l.id}" aria-label="Delete">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/>
          </svg>
        </button>
      `;
      row.querySelector('.bl-history-delete').addEventListener('click', e => {
        e.stopPropagation();
        logs = logs.filter(x => x.id !== l.id);
        saveLogs();
        renderHistory(filter);
        renderHome();
      });
      grp.appendChild(row);
    });
    list.appendChild(grp);
  });
}

function historyIcon(type) {
  const icons = {
    diaper:    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 9h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z"/><path d="M3 9c0-3 3-5 9-5s9 2 9 5"/><path d="M9 14h6"/></svg>',
    reflux:    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2c-4 4-6 7-6 10a6 6 0 0 0 12 0c0-3-2-6-6-10z"/></svg>',
    tummy_time:'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="17" cy="5" r="2"/><path d="M3 20l5-8 3 4 3-6 4 5"/></svg>',
    bath:      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2H9V6z"/><path d="M4 10h16v4a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6v-4z"/></svg>',
    potty:     '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2a5 5 0 0 1 5 5v3H7V7a5 5 0 0 1 5-5z"/><path d="M7 10v2a5 5 0 0 0 10 0v-2"/></svg>',
  };
  return icons[type] || '';
}

function historyTitle(l) {
  if (l.type === 'diaper') {
    const base = { wet: 'Wet diaper', dirty: 'Dirty diaper', both: 'Wet & dirty' }[l.diaperType] || 'Diaper';
    return l.isPullUp ? `${base} (pull-up)` : base;
  }
  if (l.type === 'reflux') {
    return { small: 'Small spit-up', medium: 'Medium spit-up', large: 'Large spit-up', projectile: 'Projectile' }[l.volume] || 'Spit-up';
  }
  if (l.type === 'tummy_time') {
    const label = prefs.activePlayMode ? 'Active play' : 'Tummy time';
    const mins  = l.durationMin ? formatActiveDuration(l.durationMin) : 'In progress';
    return `${label} · ${mins}`;
  }
  if (l.type === 'bath') {
    return { sponge: 'Sponge bath', tub: 'Tub bath', sink: 'Sink bath', shower: 'Shower' }[l.bathType] || 'Bath';
  }
  if (l.type === 'potty') {
    return { pee_success: 'Pee ✓', poo_success: 'Poo ✓', attempt: 'Potty attempt', accident: 'Accident' }[l.pottyType] || 'Potty';
  }
  return 'Log entry';
}

function historySub(l) {
  const parts = [];
  if (l.type === 'diaper') {
    if (l.diaperColor) parts.push(l.diaperColor);
    if (l.hasRash)  parts.push('rash');
    if (l.hasLeak)  parts.push('leak');
  }
  if (l.type === 'reflux') {
    if (l.position) parts.push(l.position);
    if (l.minutesAfterFeed != null) parts.push(`${l.minutesAfterFeed} min after feed`);
  }
  if (l.type === 'tummy_time') {
    if (l.position) parts.push(l.position.replace(/_/g, ' '));
    if (l.mood)     parts.push(l.mood);
  }
  if (l.type === 'bath') {
    if (l.productsUsed) parts.push(l.productsUsed.slice(0, 40));
    if (l.isBedtimeRoutine) parts.push('bedtime routine');
  }
  if (l.type === 'potty') {
    if (l.pottyLoc)  parts.push(l.pottyLoc.replace(/_/g,' '));
    if (l.pottyInit) parts.push(l.pottyInit);
  }
  if (l.notes) parts.push(l.notes.slice(0, 40));
  if (l.source === 'voice') parts.push('via voice');
  return parts.join(' · ') || formatTimeAgo(l.loggedAt || l.startTime || Date.now());
}

// ── INSIGHTS RENDER ───────────────────────────────────────────────────────────
function renderInsights() {
  const container = $('#bl-insights-content');
  if (!container) return;
  container.innerHTML = '';
  const stage = getStage();
  const cards = [];

  const diapers7  = logsOfType('diaper', 7);
  const reflux7   = logsOfType('reflux', 7);
  const active7   = logsOfType('tummy_time', 7);
  const potty14   = logsOfType('potty', 14);
  const today     = todayLogs();

  // ── Stage 1-2 cards ──
  if (stage <= 2) {
    const wet7 = diapers7.filter(l => l.diaperType === 'wet' || l.diaperType === 'both').length;
    const wetAvg = wet7 / 7;
    if (diapers7.length > 0) {
      cards.push({
        type: 'diaper',
        headline: `${wetAvg.toFixed(1)} wet diapers/day this week`,
        text: wetAvg >= 6
          ? 'Hydration looks steady — great sign.'
          : 'Slightly below 6/day. Check feeds and consider an extra nursing or bottle session.',
      });
    }
    const refluxAfterFeed = reflux7.filter(l => l.minutesAfterFeed != null && l.minutesAfterFeed <= 20).length;
    if (reflux7.length > 0) {
      cards.push({
        type: 'reflux',
        headline: `${reflux7.length} spit-up${reflux7.length !== 1 ? 's' : ''} logged this week`,
        text: refluxAfterFeed > 2
          ? 'Most spit-ups happened within 20 min of feeding — extended upright time after feeds may help.'
          : `${reflux7.length} spit-ups this week${reflux7.filter(l=>l.flagged).length ? `, ${reflux7.filter(l=>l.flagged).length} flagged` : ''}.`,
      });
    }
    if (active7.length > 0) {
      const mins   = active7.filter(l => l.durationMin).reduce((s,l) => s + l.durationMin, 0);
      const avgMin = mins / Math.max(active7.length, 1);
      cards.push({
        type: 'tummy',
        headline: `${formatActiveDuration(mins)} tummy time this week`,
        text: avgMin >= 30
          ? 'Hitting the 30-min daily goal — baby\'s core is getting stronger.'
          : `Averaging ${formatActiveDuration(avgMin)}/day — every minute builds strength.`,
      });
    }
  }

  // ── Stage 3 cards ──
  if (stage === 3) {
    const wet7  = diapers7.filter(l => l.diaperType === 'wet' || l.diaperType === 'both').length;
    const dirtyColors = diapers7.filter(l => l.diaperColor && ['green','red','pale'].includes(l.diaperColor));
    if (dirtyColors.length) {
      cards.push({
        type: 'diaper',
        headline: `${dirtyColors.length} flagged diaper colour${dirtyColors.length !== 1 ? 's' : ''} this week`,
        text: 'Green, pale, or red diapers can appear as solids begin — worth mentioning at your next appointment if they persist.',
      });
    } else if (diapers7.length > 0) {
      cards.push({
        type: 'diaper',
        headline: `${(wet7/7).toFixed(1)} wet diapers/day`,
        text: 'Diaper patterns look typical for this stage.',
      });
    }
    if (isRefluxResolving()) {
      cards.push({
        type: 'reflux',
        headline: 'Reflux seems to be improving',
        text: 'Spit-ups have reduced significantly over the past few weeks — this is normal as the lower oesophageal sphincter matures.',
      });
    }
    if (active7.length > 0) {
      const mins = active7.filter(l => l.durationMin).reduce((s,l) => s + l.durationMin, 0);
      cards.push({
        type: 'tummy',
        headline: `${formatActiveDuration(mins)} active play this week`,
        text: 'Active floor time at this stage builds the foundation for crawling and sitting. Keep it varied.',
      });
    }
  }

  // ── Stage 4-5 cards ──
  if (stage >= 4) {
    if (potty14.length >= 14) {
      const successes = potty14.filter(l => l.pottyType === 'pee_success' || l.pottyType === 'poo_success');
      const rate = Math.round((successes.length / potty14.length) * 100);
      const bestHour = getBestPottyHour(potty14.filter(l => l.pottyType === 'pee_success' || l.pottyType === 'poo_success'));
      cards.push({
        type: 'potty',
        headline: `${rate}% success rate over 14 days`,
        text: bestHour != null
          ? `${potty14.length} potty sessions logged. ${prefs.babyName || 'Baby'}'s best window appears to be around ${formatHour(bestHour)} — scheduling a trip then may help.`
          : `${potty14.length} potty sessions logged this week. Keep up the consistency.`,
      });
    } else if (potty14.length > 0) {
      cards.push({
        type: 'potty',
        headline: `${potty14.length} potty session${potty14.length !== 1 ? 's' : ''} logged`,
        text: 'Log 14+ sessions to unlock best-time-of-day insights.',
      });
    }
    const todayDiapers = today.filter(l => l.type === 'diaper').length;
    const diaperAvg7   = diapers7.length / 7;
    if (diaperAvg7 < 2 && prefs.pottyActivated) {
      cards.push({
        type: 'diaper',
        headline: 'Diaper use is tapering',
        text: `Averaging ${diaperAvg7.toFixed(1)} diapers/day — ${prefs.babyName || 'baby'} is relying on the potty more and more.`,
      });
    }
  }

  // Cross-tracker card
  const todayWet = today.filter(l => l.type === 'diaper' && (l.diaperType === 'wet' || l.diaperType === 'both')).length;
  let crossText;
  if (todayWet > 0 && todayWet < 6 && stage <= 2) {
    crossText = `${todayWet} wet diapers so far today — the target is 6+ by end of day.`;
  } else if (stage >= 4 && prefs.pottyActivated) {
    const slLogs = (() => { try { return JSON.parse(localStorage.getItem('sl_logs') || '[]'); } catch { return []; } })();
    if (slLogs.length && potty14.length >= 10) {
      crossText = `On days with longer overnight sleep (DreamLog), ${prefs.babyName || 'baby'}'s first successful potty trip tends to come earlier. Consistent wake times may support the routine.`;
    } else {
      crossText = 'Keep logging a few more days to unlock cross-tracker insights across feeding, sleep, and care.';
    }
  } else {
    crossText = 'Keep logging to unlock pattern insights across diaper, reflux, and active time.';
  }
  cards.push({ type: 'cross', headline: 'Today at a glance', text: crossText });

  cards.slice(0, 4).forEach(card => {
    const el = document.createElement('div');
    el.className = `bl-insight-card bl-insight-card--${card.type}`;
    el.innerHTML = `
      <div class="bl-insight-icon">${insightIcon(card.type)}</div>
      <div class="bl-insight-body">
        <div class="bl-insight-headline">${card.headline}</div>
        <div class="bl-insight-text">${card.text}</div>
      </div>
      <div class="bl-insight-arrow">›</div>
    `;
    container.appendChild(el);
  });

  const updated = document.createElement('div');
  updated.className = 'bl-insights-updated';
  updated.textContent = 'Based on your recent logs.';
  container.appendChild(updated);
}

function getBestPottyHour(successes) {
  if (successes.length < 5) return null;
  const counts = {};
  successes.forEach(l => {
    const h = new Date(l.loggedAt).getHours();
    counts[h] = (counts[h] || 0) + 1;
  });
  return parseInt(Object.entries(counts).sort((a,b) => b[1]-a[1])[0]?.[0] ?? '-1', 10);
}

function formatHour(h) {
  if (h < 0) return '';
  const d = new Date(); d.setHours(h, 0, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function insightIcon(type) {
  const icons = {
    diaper: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 9h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z"/><path d="M3 9c0-3 3-5 9-5s9 2 9 5"/></svg>',
    reflux: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2c-4 4-6 7-6 10a6 6 0 0 0 12 0c0-3-2-6-6-10z"/></svg>',
    tummy:  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="17" cy="5" r="2"/><path d="M3 20l5-8 3 4 3-6 4 5"/></svg>',
    bath:   '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2H9V6z"/><path d="M4 10h16v4a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6v-4z"/></svg>',
    potty:  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2a5 5 0 0 1 5 5v3H7V7a5 5 0 0 1 5-5z"/><path d="M7 10v2a5 5 0 0 0 10 0v-2"/></svg>',
    cross:  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>',
  };
  return icons[type] || icons.cross;
}

// ── DIAPER FLOW ───────────────────────────────────────────────────────────────
function openDiaperOverlay() {
  diaperType = null; diaperColor = null;
  rashOn = false; leakOn = false; pullUpOn = false;
  $$('.bl-type-btn[data-dtype]').forEach(b => b.classList.remove('bl-type-btn--active'));
  $$('.bl-swatch').forEach(s => s.classList.remove('bl-swatch--active'));
  const colorLabel = $('#bl-color-label'); if (colorLabel) colorLabel.textContent = '';
  setToggle($('#bl-rash-toggle'), false);
  setToggle($('#bl-leak-toggle'), false);
  setToggle($('#bl-pullup-toggle'), false);
  const notes = $('#bl-diaper-notes'); if (notes) notes.value = '';
  const panel = $('#bl-diaper-details'); if (panel) panel.classList.remove('bl-details-panel--open');
  updateDiaperSaveBtn();
  openOverlay('bl-overlay-diaper');
}

function updateDiaperSaveBtn() {
  const btn = $('#bl-diaper-save');
  if (!btn) return;
  if (diaperType) {
    btn.textContent = `Save — ${diaperType.charAt(0).toUpperCase() + diaperType.slice(1)} diaper`;
    btn.classList.remove('bl-save-btn--disabled');
  } else {
    btn.textContent = 'Select type above';
    btn.classList.add('bl-save-btn--disabled');
  }
}

function saveDiaper() {
  if (!diaperType) return;
  const entry = {
    id: newId(), type: 'diaper', loggedAt: Date.now(),
    diaperType, diaperColor, hasRash: rashOn, hasLeak: leakOn, isPullUp: pullUpOn,
    notes: $('#bl-diaper-notes')?.value.trim() || '', source: 'manual',
  };
  logs.push(entry); saveLogs();
  closeOverlay('bl-overlay-diaper');
  renderHome();
  showToast(`${historyTitle(entry)} logged ✓`);
}

// ── REFLUX FLOW ───────────────────────────────────────────────────────────────
function openRefluxOverlay() {
  refluxVol = null; refluxPos = null; isIllness = false;
  $$('.bl-vol-btn').forEach(b => b.classList.remove('bl-vol-btn--active'));
  $$('.bl-pos-btn').forEach(b => b.classList.remove('bl-pos-btn--active'));
  const flag = $('#bl-projectile-flag'); if (flag) flag.style.display = 'none';
  const notes = $('#bl-reflux-notes'); if (notes) notes.value = '';

  // Reset illness toggle
  $('#bl-illness-reflux-btn')?.classList.add('bl-illness-btn--active');
  $('#bl-illness-vomit-btn')?.classList.remove('bl-illness-btn--active');

  // NurseNote cross-ref
  const feedCtx = $('#bl-reflux-feed-context');
  if (feedCtx) {
    try {
      const sessions = JSON.parse(localStorage.getItem('ft_sessions') || '[]');
      if (sessions.length) {
        const last = sessions.slice().sort((a,b) => (b.endedAt||b.startedAt||0)-(a.endedAt||a.startedAt||0))[0];
        const endedAt = last.endedAt || last.startedAt;
        if (endedAt) {
          const minsAgo = Math.round((Date.now() - endedAt) / 60000);
          if (minsAgo < 180) {
            feedCtx.textContent = `⏱ ${minsAgo} min after last feed`;
            feedCtx.style.display = 'inline-flex';
          } else { feedCtx.style.display = 'none'; }
        }
      }
    } catch { feedCtx.style.display = 'none'; }
  }

  updateRefluxSaveBtn();
  openOverlay('bl-overlay-reflux');
}

function updateRefluxSaveBtn() {
  const btn = $('#bl-reflux-save');
  if (!btn) return;
  if (refluxVol) {
    btn.textContent = `Save — ${refluxVol.charAt(0).toUpperCase() + refluxVol.slice(1)}`;
    btn.classList.remove('bl-save-btn--disabled');
  } else {
    btn.textContent = 'Select amount above';
    btn.classList.add('bl-save-btn--disabled');
  }
}

function saveReflux() {
  if (!refluxVol) return;
  let minutesAfterFeed;
  try {
    const sessions = JSON.parse(localStorage.getItem('ft_sessions') || '[]');
    if (sessions.length) {
      const last = sessions.slice().sort((a,b)=>(b.endedAt||b.startedAt||0)-(a.endedAt||a.startedAt||0))[0];
      const endedAt = last.endedAt || last.startedAt;
      if (endedAt) minutesAfterFeed = Math.round((Date.now() - endedAt) / 60000);
    }
  } catch { /* ignore */ }

  const entry = {
    id: newId(), type: 'reflux', loggedAt: Date.now(),
    volume: refluxVol, flagged: refluxVol === 'projectile',
    position: refluxPos || null, minutesAfterFeed: minutesAfterFeed || null,
    isIllness, notes: $('#bl-reflux-notes')?.value.trim() || '', source: 'manual',
  };
  logs.push(entry); saveLogs();
  closeOverlay('bl-overlay-reflux');
  renderHome();
  const label = { small: 'Small spit-up', medium: 'Medium spit-up', large: 'Large spit-up', projectile: 'Projectile spit-up' }[refluxVol];
  showToast(`${label} logged ✓${entry.flagged ? ' — flagged' : ''}${isIllness ? ' — illness' : ''}`);
}

// ── ACTIVE TIME FLOW (Tummy Time / Active Play) — drift-proof ─────────────────
function startActiveTime() {
  openTummyOverlay(); // overlay handles both idle (start/log-past) and active (timer) modes
}

function openTummyOverlay() {
  activeTT = loadActiveTT();
  activePos = null; activeMood = null;
  $$('.bl-tummy-pos-btn').forEach(b => b.classList.remove('bl-tummy-pos-btn--active'));
  $$('.bl-tummy-mood-btn').forEach(b => b.classList.remove('bl-tummy-mood-btn--active'));
  _syncTummyMode();
  openOverlay('bl-overlay-tummy');
  if (activeTT) { updateTummyDisplay(); startTummyTick(); }
}

function _syncTummyMode() {
  const isLive = !!activeTT;
  const logPastEl = $('#bl-tummy-log-past');
  const endBtn    = $('#bl-tummy-end-btn');
  const closeBtn  = $('#bl-tummy-overlay-close');
  const display   = $('#bl-tummy-timer-display');
  const statusEl  = $('#bl-tummy-timer-status');
  const arc       = $('#bl-tummy-timer-arc');
  if (logPastEl) logPastEl.style.display = isLive ? 'none' : '';
  if (endBtn)    endBtn.style.display    = isLive ? '' : 'none';
  if (closeBtn)  closeBtn.textContent    = isLive ? 'Minimise' : 'Cancel';
  if (!isLive) {
    if (display)  display.textContent  = '0:00';
    if (statusEl) statusEl.textContent = 'TAP TO START';
    if (arc) { arc.style.strokeDasharray = '502.65'; arc.style.strokeDashoffset = '502.65'; }
  }
}

function startTummyTick() {
  if (ttTick) clearInterval(ttTick);
  ttTick = setInterval(updateTummyDisplay, 500);
}

function updateTummyDisplay() {
  activeTT = loadActiveTT();
  if (!activeTT) { clearInterval(ttTick); return; }
  const stage    = getStage();
  const goal     = getActiveGoal(stage);
  const elapsedMs = Date.now() - activeTT.startedAt;
  const display   = $('#bl-tummy-timer-display');
  if (display) display.textContent = msToMMSS(elapsedMs);
  const statusEl = $('#bl-tummy-timer-status');
  if (statusEl) statusEl.textContent = 'TAP TO STOP';

  const circ  = 2 * Math.PI * 80;
  const maxMs = 10 * 60 * 1000;
  const arc   = $('#bl-tummy-timer-arc');
  if (arc) {
    arc.style.strokeDasharray  = circ;
    arc.style.strokeDashoffset = circ * (1 - Math.min(elapsedMs / maxMs, 1) % 1);
  }

  const completedMins = todayLogs().filter(l => l.type === 'tummy_time' && l.durationMin)
    .reduce((s, l) => s + l.durationMin, 0);
  const totalMins = completedMins + elapsedMs / 60000;
  const dailyText = $('#bl-tummy-daily-text');
  if (dailyText) dailyText.textContent = `${formatActiveDuration(totalMins)} / ${goal} min today`;
}

function endActiveTime() {
  activeTT = loadActiveTT();
  if (!activeTT) { closeOverlay('bl-overlay-tummy'); return; }
  if (ttTick) { clearInterval(ttTick); ttTick = null; }

  const elapsedMs  = Date.now() - activeTT.startedAt;
  const durationMin = elapsedMs / 60000;
  if (durationMin < 0.1) {
    saveActiveTT(null); activeTT = null;
    closeOverlay('bl-overlay-tummy');
    renderHome();
    showToast('Session too short — not saved.');
    return;
  }

  const label = (getStage() >= 3 && prefs.activePlayMode) ? 'Active play' : 'Tummy time';
  const entry = {
    id: activeTT.id, type: 'tummy_time',
    startTime: activeTT.startedAt, endTime: Date.now(),
    loggedAt: activeTT.startedAt, durationMin,
    position: activePos || null, mood: activeMood || null,
    logMode: prefs.activePlayMode ? 'active_play' : 'tummy_time',
    source: 'manual',
  };
  logs.push(entry); saveLogs();
  saveActiveTT(null); activeTT = null;
  closeOverlay('bl-overlay-tummy');
  renderHome();
  showToast(`${label}: ${formatActiveDuration(durationMin)} logged ✓`);
}

// ── BATH FLOW ─────────────────────────────────────────────────────────────────
function openBathOverlay() {
  bathType = 'sponge'; bathRoutine = false;
  $$('.bl-btype-btn').forEach(b => b.classList.toggle('bl-btype-btn--active', b.dataset.btype === 'sponge'));
  setToggle($('#bl-bath-routine-toggle'), false);
  const prod  = $('#bl-bath-products'); if (prod)  prod.value  = '';
  const notes = $('#bl-bath-notes');    if (notes) notes.value = '';
  openOverlay('bl-overlay-bath');
}

function saveBath() {
  const entry = {
    id: newId(), type: 'bath', loggedAt: Date.now(),
    bathType, productsUsed: $('#bl-bath-products')?.value.trim() || '',
    isBedtimeRoutine: bathRoutine,
    notes: $('#bl-bath-notes')?.value.trim() || '', source: 'manual',
  };
  // Cross-log with DreamLog if bedtime routine
  if (bathRoutine) {
    try {
      const slRoutines = JSON.parse(localStorage.getItem('sl_routines') || '[]');
      slRoutines.push({ id: newId(), loggedAt: Date.now(), source: 'babylog_bath', bathLogId: entry.id });
      localStorage.setItem('sl_routines', JSON.stringify(slRoutines));
    } catch { /* ignore */ }
  }
  logs.push(entry); saveLogs();
  closeOverlay('bl-overlay-bath');
  renderHome();
  const label = { sponge: 'Sponge bath', tub: 'Tub bath', sink: 'Sink bath', shower: 'Shower' }[bathType] || 'Bath';
  showToast(`${label} logged ✓`);
}

// ── POTTY FLOW ────────────────────────────────────────────────────────────────
function openPottyOverlay() {
  pottyType = null; pottyLoc = 'potty'; pottyInit = 'parent';
  $$('.bl-potty-type-btn').forEach(b => b.classList.remove('bl-potty-type-btn--active'));
  $$('.bl-ploc-btn[data-loc]').forEach((b, i) => b.classList.toggle('bl-ploc-btn--active', i === 0));
  $$('.bl-ploc-btn[data-init]').forEach((b, i) => b.classList.toggle('bl-ploc-btn--active', i === 0));
  const notes = $('#bl-potty-notes'); if (notes) notes.value = '';
  const panel = $('#bl-potty-details'); if (panel) panel.classList.remove('bl-details-panel--open');
  updatePottySaveBtn();
  openOverlay('bl-overlay-potty');
}

function updatePottySaveBtn() {
  const btn = $('#bl-potty-save');
  if (!btn) return;
  if (pottyType) {
    const labels = { pee_success: 'Save — Pee ✓', poo_success: 'Save — Poo ✓', attempt: 'Save — Attempt', accident: 'Save — Accident' };
    btn.textContent = labels[pottyType] || 'Save';
    btn.classList.remove('bl-save-btn--disabled');
  } else {
    btn.textContent = 'Select above';
    btn.classList.add('bl-save-btn--disabled');
  }
}

function savePotty() {
  if (!pottyType) return;
  const entry = {
    id: newId(), type: 'potty', loggedAt: Date.now(),
    pottyType, pottyLoc, pottyInit,
    notes: $('#bl-potty-notes')?.value.trim() || '', source: 'manual',
  };
  logs.push(entry); saveLogs();
  closeOverlay('bl-overlay-potty');
  renderHome();
  const labels = { pee_success: 'Pee success', poo_success: 'Poo success', attempt: 'Potty attempt', accident: 'Accident' };
  showToast(`${labels[pottyType] || 'Potty'} logged ✓`);
}

// ── SETTINGS FLOW ─────────────────────────────────────────────────────────────
function openSettings() {
  const n = $('#bl-settings-name');       if (n) n.value = prefs.babyName || '';
  const b = $('#bl-settings-birthdate');  if (b) b.value = prefs.birthdate || '';
  const g = $('#bl-settings-gestational'); if (g) g.value = prefs.gestationalAge || 40;
  const goal = $('#bl-settings-goal');
  if (goal) goal.value = prefs.activePlayGoalMin || getActiveGoal(getStage());
  setToggle($('#bl-settings-potty-toggle'), prefs.pottyActivated);
  setToggle($('#bl-settings-diaper-toggle'), !prefs.diaperArchived);
  openOverlay('bl-overlay-settings');
}

function saveSettings() {
  const n = $('#bl-settings-name')?.value.trim();
  const b = $('#bl-settings-birthdate')?.value;
  const g = parseInt($('#bl-settings-gestational')?.value || '40', 10);
  const goalVal = parseInt($('#bl-settings-goal')?.value || '0', 10);
  const pottyOn  = $('#bl-settings-potty-toggle')?.dataset.on === 'true';
  const diaperOn = $('#bl-settings-diaper-toggle')?.dataset.on === 'true';

  if (n) prefs.babyName = n;
  if (b) prefs.birthdate = b;
  prefs.gestationalAge = g || 40;
  if (goalVal > 0) { prefs.activePlayGoalMin = goalVal; prefs.activePlayGoalCustom = true; }
  if (pottyOn !== prefs.pottyActivated) {
    prefs.pottyActivated = pottyOn;
    if (pottyOn) { const ph = $('#bl-potty-hero'); if (ph && getStage() >= 4) ph.style.display = 'block'; }
  }
  prefs.diaperArchived = !diaperOn;
  savePrefs();
  closeOverlay('bl-overlay-settings');
  applyStageUI(getStage());
  renderHome();
  showToast('Settings saved ✓');
}

function downloadCSV() {
  const headers = ['type','loggedAt','diaperType','volume','durationMin','bathType','pottyType','notes'];
  const rows = logs.map(l => headers.map(h => {
    const v = l[h];
    if (v == null) return '';
    if (typeof v === 'string' && v.includes(',')) return `"${v}"`;
    if (h === 'loggedAt') return new Date(v).toISOString();
    return v;
  }).join(','));
  const csv  = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `babylog-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── VOICE LOGGING ─────────────────────────────────────────────────────────────
function openVoiceOverlay() {
  const status = $('#bl-voice-status'); if (status) status.textContent = 'Tap and speak';
  const trans  = $('#bl-voice-transcript');
  if (trans) { trans.textContent = ''; trans.classList.remove('bl-voice-transcript--result'); }
  openOverlay('bl-overlay-voice');
}

function startVoiceListening() {
  if (!SpeechRecognitionCtor) { showToast('Voice not supported in this browser.'); return; }
  const orb    = $('#bl-voice-orb');
  const status = $('#bl-voice-status');
  if (orb) orb.classList.add('bl-voice-orb--listening');
  if (status) status.textContent = 'Listening…';
  if (voiceRec) voiceRec.abort();
  voiceRec = new SpeechRecognitionCtor();
  voiceRec.lang = 'en-US';
  voiceRec.continuous = false;
  voiceRec.interimResults = false;
  voiceRec.onresult = (e) => {
    const text  = e.results[0][0].transcript.toLowerCase().trim();
    const trans = $('#bl-voice-transcript');
    if (trans) { trans.textContent = text; trans.classList.add('bl-voice-transcript--result'); }
    if (status) status.textContent = 'Got it!';
    if (orb) orb.classList.remove('bl-voice-orb--listening');
    const action = parseVoiceIntent(text);
    if (action) {
      setTimeout(() => { closeOverlay('bl-overlay-voice'); action(); }, 800);
    } else {
      if (status) status.textContent = 'Try again — not sure what to log.';
    }
  };
  voiceRec.onerror = () => { if (orb) orb.classList.remove('bl-voice-orb--listening'); if (status) status.textContent = 'Couldn\'t hear that. Try again.'; };
  voiceRec.onend   = () => { if (orb) orb.classList.remove('bl-voice-orb--listening'); };
  voiceRec.start();
}

function parseVoiceIntent(text) {
  // Diaper
  if (/wet\s*diaper|just\s*wet/.test(text))                     return () => quickLogDiaper('wet');
  if (/dirty\s*diaper|poop(ed)?|dirty/.test(text))              return () => quickLogDiaper('dirty');
  if (/both|wet\s*and\s*dirty/.test(text))                      return () => quickLogDiaper('both');
  if (/diaper/.test(text))                                       return () => quickLogDiaper('wet');
  // Reflux
  if (/projectile/.test(text))                                   return () => quickLogReflux('projectile');
  if (/large|a\s*lot|threw?\s*up|vomit/.test(text))             return () => quickLogReflux('large');
  if (/medium/.test(text))                                       return () => quickLogReflux('medium');
  if (/small|little/.test(text))                                 return () => quickLogReflux('small');
  if (/spit.?up|reflux|spit/.test(text))                        return () => quickLogReflux('small');
  // Active time
  const ttMinMatch = text.match(/(\d+)\s*min(?:ute)?s?\s*(?:of\s+)?(?:tummy|active|play)/);
  if (ttMinMatch)                                                 return () => quickLogActiveTime(parseInt(ttMinMatch[1], 10));
  if (/active\s*play\s*done|active\s*done/.test(text))           return () => endActiveTime();
  if (/tummy\s*time\s*done|tummy\s*done/.test(text))             return () => endActiveTime();
  if (/tummy\s*time|tummy\s*start|active\s*play\s*start/.test(text)) return () => startActiveTime();
  // Bath
  if (/bath/.test(text))                                         return () => quickLogBath();
  // Potty (stage 4-5)
  if (/made\s*it|made\s*the\s*potty|pee\s*success/.test(text))  return () => quickLogPotty('pee_success');
  if (/poo\s*success|pooped\s*in\s*the\s*potty/.test(text))     return () => quickLogPotty('poo_success');
  if (/tried\s*the\s*potty|attempted|nothing\s*happened/.test(text)) return () => quickLogPotty('attempt');
  if (/accident|didn.t\s*make\s*it/.test(text))                 return () => quickLogPotty('accident');
  return null;
}

function quickLogDiaper(type) {
  const entry = { id: newId(), type: 'diaper', loggedAt: Date.now(), diaperType: type, hasRash: false, hasLeak: false, source: 'voice' };
  logs.push(entry); saveLogs(); renderHome();
  showToast(`${historyTitle(entry)} logged via voice ✓`);
}
function quickLogReflux(vol) {
  const entry = { id: newId(), type: 'reflux', loggedAt: Date.now(), volume: vol, flagged: vol === 'projectile', source: 'voice' };
  logs.push(entry); saveLogs(); renderHome();
  showToast(`${historyTitle(entry)} logged via voice ✓`);
}
function quickLogActiveTime(mins) {
  const entry = { id: newId(), type: 'tummy_time', startTime: Date.now() - mins*60000, endTime: Date.now(), loggedAt: Date.now() - mins*60000, durationMin: mins, source: 'voice' };
  logs.push(entry); saveLogs(); renderHome();
  const label = (getStage() >= 3 && prefs.activePlayMode) ? 'Active play' : 'Tummy time';
  showToast(`${label}: ${mins} min logged via voice ✓`);
}
function quickLogBath() {
  const entry = { id: newId(), type: 'bath', loggedAt: Date.now(), bathType: 'sponge', source: 'voice' };
  logs.push(entry); saveLogs(); renderHome();
  showToast('Bath logged via voice ✓');
}
function quickLogPotty(type) {
  if (!prefs.pottyActivated) { showToast('Enable potty tracking in Settings first.'); return; }
  const entry = { id: newId(), type: 'potty', loggedAt: Date.now(), pottyType: type, source: 'voice' };
  logs.push(entry); saveLogs(); renderHome();
  const labels = { pee_success: 'Pee success', poo_success: 'Poo success', attempt: 'Potty attempt', accident: 'Accident' };
  showToast(`${labels[type] || 'Potty'} logged via voice ✓`);
}

// ── SHARED HELPERS ────────────────────────────────────────────────────────────
function setToggle(btn, on) {
  if (!btn) return;
  btn.dataset.on = on ? 'true' : 'false';
  btn.textContent = on ? 'Yes' : 'No';
  btn.classList.toggle('bl-toggle-btn--on', on);
}

// ── INIT ──────────────────────────────────────────────────────────────────────
async function init() {
  loadLogs();
  loadPrefs();
  activeTT = loadActiveTT();

  await hydrateFromSharedProfile();

  // Child selector
  $('#bl-child-selector-btn')?.addEventListener('click', switchLogChild);

  // Onboarding
  checkOnboarding();
  $('#bl-ob-save')?.addEventListener('click', saveOnboarding);
  $('#bl-ob-close')?.addEventListener('click', () => { const ob = $('#bl-onboarding'); if (ob) ob.classList.remove('bl-onboarding--active'); });

  // Apply stage UI
  const stage = getStage();
  applyStageUI(stage);

  // Bottom nav
  $$('.bl-nav-item[data-screen]').forEach(btn => {
    btn.addEventListener('click', () => navigate(btn.dataset.screen));
  });

  // + nav button
  $('#bl-nav-add')?.addEventListener('click', () => openOverlay('bl-overlay-picker'));

  // Picker backdrop
  $('#bl-picker-backdrop')?.addEventListener('click', () => closeOverlay('bl-overlay-picker'));
  $('#bl-more-backdrop')?.addEventListener('click',   () => closeOverlay('bl-overlay-more'));

  // Picker buttons
  $$('.bl-pick-btn[data-log]').forEach(btn => {
    btn.addEventListener('click', () => {
      closeOverlay('bl-overlay-picker');
      const log = btn.dataset.log;
      if (log === 'diaper') openDiaperOverlay();
      if (log === 'reflux') openRefluxOverlay();
      if (log === 'tummy')  startActiveTime();
      if (log === 'bath')   openBathOverlay();
      if (log === 'potty')  openPottyOverlay();
    });
  });
  $('#bl-pick-voice')?.addEventListener('click', () => { closeOverlay('bl-overlay-picker'); openVoiceOverlay(); startVoiceListening(); });

  // Grid buttons (stage 1-3)
  $('#bl-btn-diaper')?.addEventListener('click', openDiaperOverlay);
  $('#bl-btn-reflux')?.addEventListener('click', openRefluxOverlay);
  $('#bl-btn-tummy')?.addEventListener('click',  startActiveTime);
  $('#bl-btn-bath')?.addEventListener('click',   openBathOverlay);

  // Potty hero log button
  $('#bl-btn-log-potty')?.addEventListener('click', openPottyOverlay);

  // Stage 4-5 secondary grid
  $('#bl-sec-btn-diaper')?.addEventListener('click', openDiaperOverlay);
  $('#bl-sec-btn-tummy')?.addEventListener('click',  startActiveTime);
  $('#bl-sec-btn-bath')?.addEventListener('click',   openBathOverlay);

  // More drawer
  $('#bl-more-btn')?.addEventListener('click', () => openOverlay('bl-overlay-more'));
  $$('.bl-more-item[data-log]').forEach(btn => {
    btn.addEventListener('click', () => {
      closeOverlay('bl-overlay-more');
      if (btn.dataset.log === 'reflux') openRefluxOverlay();
    });
  });

  // Settings
  $('#bl-topbar-settings')?.addEventListener('click', openSettings);
  $('#bl-settings-close')?.addEventListener('click',  () => closeOverlay('bl-overlay-settings'));
  $('#bl-settings-save')?.addEventListener('click',   saveSettings);
  $('#bl-settings-csv')?.addEventListener('click',    downloadCSV);
  $('#bl-settings-potty-toggle')?.addEventListener('click', (e) => {
    const on = e.target.dataset.on !== 'true';
    setToggle(e.target, on);
  });
  $('#bl-settings-diaper-toggle')?.addEventListener('click', (e) => {
    const on = e.target.dataset.on !== 'true';
    setToggle(e.target, on);
  });

  // ── Diaper overlay ──
  $('#bl-diaper-close')?.addEventListener('click', () => closeOverlay('bl-overlay-diaper'));
  $$('.bl-type-btn[data-dtype]').forEach(btn => {
    btn.addEventListener('click', () => {
      diaperType = btn.dataset.dtype;
      $$('.bl-type-btn[data-dtype]').forEach(b => b.classList.remove('bl-type-btn--active'));
      btn.classList.add('bl-type-btn--active');
      updateDiaperSaveBtn();
    });
  });
  const detailsToggle = $('#bl-diaper-details-toggle');
  const detailsPanel  = $('#bl-diaper-details');
  if (detailsToggle && detailsPanel) {
    detailsToggle.addEventListener('click', () => {
      const open = detailsPanel.classList.toggle('bl-details-panel--open');
      detailsToggle.textContent = open ? 'Hide details ›' : 'Add details ›';
    });
  }
  $$('.bl-swatch').forEach(swatch => {
    swatch.addEventListener('click', () => {
      diaperColor = swatch.dataset.color;
      $$('.bl-swatch').forEach(s => s.classList.remove('bl-swatch--active'));
      swatch.classList.add('bl-swatch--active');
      const label = $('#bl-color-label'); if (label) label.textContent = swatch.dataset.label || '';
    });
  });
  const rashBtn   = $('#bl-rash-toggle');   if (rashBtn)   rashBtn.addEventListener('click',   () => { rashOn   = !rashOn;   setToggle(rashBtn,   rashOn); });
  const leakBtn   = $('#bl-leak-toggle');   if (leakBtn)   leakBtn.addEventListener('click',   () => { leakOn   = !leakOn;   setToggle(leakBtn,   leakOn); });
  const pullupBtn = $('#bl-pullup-toggle'); if (pullupBtn) pullupBtn.addEventListener('click', () => { pullUpOn = !pullUpOn; setToggle(pullupBtn, pullUpOn); });
  $('#bl-diaper-save')?.addEventListener('click', saveDiaper);

  // ── Reflux overlay ──
  $('#bl-reflux-close')?.addEventListener('click', () => closeOverlay('bl-overlay-reflux'));
  $$('.bl-vol-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      refluxVol = btn.dataset.vol;
      $$('.bl-vol-btn').forEach(b => b.classList.remove('bl-vol-btn--active'));
      btn.classList.add('bl-vol-btn--active');
      const flag = $('#bl-projectile-flag'); if (flag) flag.style.display = refluxVol === 'projectile' ? 'block' : 'none';
      updateRefluxSaveBtn();
    });
  });
  $$('.bl-pos-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      refluxPos = btn.dataset.pos;
      $$('.bl-pos-btn').forEach(b => b.classList.remove('bl-pos-btn--active'));
      btn.classList.add('bl-pos-btn--active');
    });
  });
  $('#bl-illness-reflux-btn')?.addEventListener('click', () => {
    isIllness = false;
    $('#bl-illness-reflux-btn')?.classList.add('bl-illness-btn--active');
    $('#bl-illness-vomit-btn')?.classList.remove('bl-illness-btn--active');
  });
  $('#bl-illness-vomit-btn')?.addEventListener('click', () => {
    isIllness = true;
    $('#bl-illness-vomit-btn')?.classList.add('bl-illness-btn--active');
    $('#bl-illness-reflux-btn')?.classList.remove('bl-illness-btn--active');
  });
  $('#bl-reflux-save')?.addEventListener('click', saveReflux);

  // ── Active time overlay ──
  $('#bl-tummy-overlay-close')?.addEventListener('click', () => closeOverlay('bl-overlay-tummy'));
  $('#bl-tummy-end-btn')?.addEventListener('click', endActiveTime);

  // Big circle = start (when idle) or stop (when active)
  $('#bl-tummy-timer-wrap')?.addEventListener('click', () => {
    if (activeTT) {
      endActiveTime();
    } else {
      activeTT = { id: newId(), startedAt: Date.now() };
      saveActiveTT(activeTT);
      renderHome();
      _syncTummyMode();
      updateTummyDisplay();
      startTummyTick();
    }
  });

  // Log-past stepper
  let _logPastMin = 10;
  const _logPastValEl = $('#bl-log-past-val');
  $('#bl-log-past-minus')?.addEventListener('click', () => {
    _logPastMin = Math.max(1, _logPastMin - 5);
    if (_logPastValEl) _logPastValEl.textContent = _logPastMin;
  });
  $('#bl-log-past-plus')?.addEventListener('click', () => {
    _logPastMin = Math.min(120, _logPastMin + 5);
    if (_logPastValEl) _logPastValEl.textContent = _logPastMin;
  });
  $('#bl-log-past-save')?.addEventListener('click', () => {
    const durationMin = _logPastMin;
    const label = (getStage() >= 3 && prefs.activePlayMode) ? 'Active play' : 'Tummy time';
    const now = Date.now();
    const entry = {
      id: newId(), type: 'tummy_time',
      startTime: now - durationMin * 60000, endTime: now,
      loggedAt: now - durationMin * 60000, durationMin,
      position: activePos || null, mood: activeMood || null,
      logMode: prefs.activePlayMode ? 'active_play' : 'tummy_time',
      source: 'manual',
    };
    logs.push(entry); saveLogs();
    closeOverlay('bl-overlay-tummy');
    renderHome();
    showToast(`${label}: ${formatActiveDuration(durationMin)} logged ✓`);
    _logPastMin = 10;
    if (_logPastValEl) _logPastValEl.textContent = _logPastMin;
  });
  $$('.bl-tummy-pos-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      activePos = btn.dataset.pos;
      $$('.bl-tummy-pos-btn').forEach(b => b.classList.remove('bl-tummy-pos-btn--active'));
      btn.classList.add('bl-tummy-pos-btn--active');
    });
  });
  $$('.bl-tummy-mood-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      activeMood = btn.dataset.mood;
      $$('.bl-tummy-mood-btn').forEach(b => b.classList.remove('bl-tummy-mood-btn--active'));
      btn.classList.add('bl-tummy-mood-btn--active');
    });
  });

  // ── Bath overlay ──
  $('#bl-bath-close')?.addEventListener('click', () => closeOverlay('bl-overlay-bath'));
  $$('.bl-btype-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      bathType = btn.dataset.btype;
      $$('.bl-btype-btn').forEach(b => b.classList.toggle('bl-btype-btn--active', b.dataset.btype === bathType));
    });
  });
  const routineToggle = $('#bl-bath-routine-toggle');
  if (routineToggle) routineToggle.addEventListener('click', () => { bathRoutine = !bathRoutine; setToggle(routineToggle, bathRoutine); });
  $('#bl-bath-save')?.addEventListener('click', saveBath);

  // ── Potty overlay ──
  $('#bl-potty-close')?.addEventListener('click', () => closeOverlay('bl-overlay-potty'));
  $$('.bl-potty-type-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      pottyType = btn.dataset.ptype;
      $$('.bl-potty-type-btn').forEach(b => b.classList.remove('bl-potty-type-btn--active'));
      btn.classList.add('bl-potty-type-btn--active');
      updatePottySaveBtn();
    });
  });
  const pottyDetailsToggle = $('#bl-potty-details-toggle');
  const pottyDetailsPanel  = $('#bl-potty-details');
  if (pottyDetailsToggle && pottyDetailsPanel) {
    pottyDetailsToggle.addEventListener('click', () => {
      const open = pottyDetailsPanel.classList.toggle('bl-details-panel--open');
      pottyDetailsToggle.textContent = open ? 'Hide details ›' : 'Add details ›';
    });
  }
  $$('.bl-ploc-btn[data-loc]').forEach(btn => {
    btn.addEventListener('click', () => {
      pottyLoc = btn.dataset.loc;
      $$('.bl-ploc-btn[data-loc]').forEach(b => b.classList.remove('bl-ploc-btn--active'));
      btn.classList.add('bl-ploc-btn--active');
    });
  });
  $$('.bl-ploc-btn[data-init]').forEach(btn => {
    btn.addEventListener('click', () => {
      pottyInit = btn.dataset.init;
      $$('.bl-ploc-btn[data-init]').forEach(b => b.classList.remove('bl-ploc-btn--active'));
      btn.classList.add('bl-ploc-btn--active');
    });
  });
  $('#bl-potty-save')?.addEventListener('click', savePotty);

  // ── Voice overlay ──
  $('#bl-nav-voice')?.addEventListener('click', () => { openVoiceOverlay(); startVoiceListening(); });
  $('#bl-voice-close')?.addEventListener('click', () => { if (voiceRec) voiceRec.abort(); closeOverlay('bl-overlay-voice'); });
  $('#bl-voice-orb')?.addEventListener('click', startVoiceListening);

  // ── History filter chips ──
  $$('.bl-filter-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      $$('.bl-filter-chip').forEach(c => c.classList.remove('bl-filter-chip--active'));
      chip.classList.add('bl-filter-chip--active');
      renderHistory(chip.dataset.filter);
    });
  });

  // Stage 3: auto-switch to active play if age crossed and not yet done
  if (stage >= 3 && !prefs.activePlayMode) {
    prefs.activePlayMode = true;
    savePrefs();
    applyStageUI(stage);
  }

  // Resume tummy tick if active
  if (activeTT) startTummyTick();

  // Initial render
  renderHome();

  // Hydrate from cloud, then sync local data up if the metafield was empty.
  loadFromCloud().then(() => { renderHome(); syncToCloud(); });
}

// ── Boot ──────────────────────────────────────────────────────────────────────
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
